import { useEffect, type ReactNode } from 'react'
import { useBranding } from '../hooks/use-branding'
import { THEME_PRIMARY_FALLBACK, THEME_PRIMARY_HOVER_FALLBACK } from './ui/styles'

function shadeHex(hex: string, pct: number): string {
  // Lightweight darken-by-pct for the hover variant. Skips anything
  // that doesn't look like #rrggbb — falls back to the static hover.
  const m = /^#?([0-9a-f]{6})$/i.exec(hex)
  if (!m) return THEME_PRIMARY_HOVER_FALLBACK
  const n = Number.parseInt(m[1], 16)
  const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 0xff) * (1 - pct))))
  const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 0xff) * (1 - pct))))
  const b = Math.max(0, Math.min(255, Math.round((n & 0xff) * (1 - pct))))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

// §7.5.2: reads the active org's themeColor via useBranding and writes
// the result to `--theme-primary` / `--theme-primary-hover` on the
// document root. Every `colors.primary` / `colors.primaryHover`
// reference in ui/styles.ts resolves against those variables, so the
// whole app themes without a render cascade. V1 uses the server-side
// "first org" fallback (single-tenant edge is the common case); a
// multi-tenant user with an explicit orgId is a follow-up once the
// auth context carries one.
export function ThemeProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const branding = useBranding()

  useEffect(() => {
    const root = document.documentElement
    const primary = branding.themeColor ?? THEME_PRIMARY_FALLBACK
    const hover = branding.themeColor ? shadeHex(branding.themeColor, 0.15) : THEME_PRIMARY_HOVER_FALLBACK
    root.style.setProperty('--theme-primary', primary)
    root.style.setProperty('--theme-primary-hover', hover)
  }, [branding.themeColor])

  return <>{children}</>
}
